/** @jsxImportSource @emotion/react */
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import FetchStatus from '../../Data/FetchStatus';
import ClientApiScreens from '../../Data/ClientApiScreens';
import { useClientApiInactivityTimer } from '../../Hooks/useClientApiInactivityTimer';
import { initialiseJourney } from '../../Lib/redux/slices/clientApiJourney';
import { selectHasBookableLocations } from '../../Helpers/clientApiLocations.helpers';
import {
  selectHasLeadStatusFailed,
  selectLeadStatusMessageKey,
} from '../../Helpers/leadStatus.helpers';
import { selectMatchedStrategyCollection } from '../../Helpers/strategyCollection.helpers';
import { getJourneySteps } from '../../Helpers/strategyDag.helpers';
import {
  buildScreenSequence,
  isOnJourneyEndScreen,
  selectCurrentScreen,
} from '../../Helpers/clientApiJourneyPosition.helpers';
import { useFallThroughToNormalLead } from '../../Hooks/useFallThroughToNormalLead';
import { FALL_THROUGH_REASONS } from './clientApiJourney.constants';
import PageFade from './PageFade';
import JourneyProgress from './JourneyProgress';
import FailureNotice from './FailureNotice';
import InactivityPrompt from './InactivityPrompt';
import StoreList from './StoreList';
import AddressList from './AddressList';
import SolarPotential from './SolarPotential';
import PostSubmissionQuestions from './PostSubmissionQuestions';
import SlotPicker from './SlotPicker';
import SolarSlots from './SolarSlots';
import ReviewBooking from './ReviewBooking';
import BookingConfirmation from './BookingConfirmation';
import useClientApiCopy from './copy/useClientApiCopy';
import useLocationStepCopy from './copy/useLocationStepCopy';
import { getLeadFailedCopy } from './copy/clientApiCopy';

// The one place a screen id becomes a component. Data/ClientApiScreens.js
// decides which screens a step type has; this decides what each one draws.
const SCREEN_COMPONENTS = {
  [ClientApiScreens.LOCATION_LIST]: StoreList,
  [ClientApiScreens.ADDRESS_LIST]: AddressList,
  [ClientApiScreens.SOLAR_POTENTIAL]: SolarPotential,
  [ClientApiScreens.POST_SUBMISSION_QUESTIONS]: PostSubmissionQuestions,
  [ClientApiScreens.SLOT_PICKER]: SlotPicker,
  [ClientApiScreens.SLOTS_BY_DAY]: SolarSlots,
  [ClientApiScreens.REVIEW_BOOKING]: ReviewBooking,
  [ClientApiScreens.CONFIRMATION]: BookingConfirmation,
};

const journeyFallThroughReason = ({
  journeyTimedOut,
  hasLeadFailedBeforeBooking,
  locationsStatus,
}) => {
  if (journeyTimedOut) return FALL_THROUGH_REASONS.TIMED_OUT;
  if (hasLeadFailedBeforeBooking)
    return FALL_THROUGH_REASONS.LEAD_STATUS_FAILED;
  if (locationsStatus === FetchStatus.FAILED)
    return FALL_THROUGH_REASONS.LOCATION_LOOKUP_FAILED;
  return FALL_THROUGH_REASONS.NO_LOCATIONS;
};

/**
 * Walks the matched collection's dag one screen at a time. Journey-wide exits
 * (a lookup failure, a timeout, Outreach failing the lead) are decided here;
 * screens flag their own dead ends through useFallThroughToNormalLead, so every
 * exit stops the timer and progress bar the same way.
 */
const ClientApiJourney = ({ componentId, iFrameId }) => {
  const dispatch = useDispatch();
  const copy = useClientApiCopy();
  const locationStepCopy = useLocationStepCopy();

  // ClientApi only renders this journey once selectChameleonStrategyCollection
  // (via hasRunnableJourney) has confirmed the dag walks and every step has
  // screens, so getJourneySteps cannot throw here.
  const collection = useSelector(selectMatchedStrategyCollection);
  const screenSequence = useSelector(
    (store) => store.clientApiJourney.screenSequence
  );
  const position = useSelector((store) => store.clientApiJourney.position);
  const currentScreen = useSelector(selectCurrentScreen);
  const locationsStatus = useSelector(
    (store) => store.clientApiJourney.location?.listStatus
  );
  const bookingStatus = useSelector(
    (store) => store.clientApiJourney.appointment?.bookingStatus
  );
  const bookingOutcome = useSelector(
    (store) => store.clientApiJourney.appointment?.bookingOutcome
  );
  const hasBookableLocations = useSelector(selectHasBookableLocations);
  const journeyTimedOut = useSelector(
    (store) => store.clientApiJourney.journeyTimedOut
  );
  const hasLeadStatusFailed = useSelector(selectHasLeadStatusFailed);
  const leadStatusMessageKey = useSelector(selectLeadStatusMessageKey);
  const isFallingThrough = useSelector(
    (store) => store.clientApiJourney.isFallingThrough
  );

  useEffect(() => {
    if (screenSequence?.length) return;

    const steps = getJourneySteps(collection);
    dispatch(
      initialiseJourney({
        stepOrder: steps.map(({ key }) => key),
        screenSequence: buildScreenSequence(steps),
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hasNoLocationsFound =
    locationsStatus === FetchStatus.COMPLETED && !hasBookableLocations;
  const hasNoLocationToBookAt =
    locationsStatus === FetchStatus.FAILED || hasNoLocationsFound;

  // Unmount is not enough of a stop signal: the confirmation, the failed states
  // and both fall-through surfaces all render inside a still-mounted journey.
  // The same flag gates the failed-lead exit below.
  const isBookingInFlightOrSettled = [
    FetchStatus.IN_PROGRESS,
    FetchStatus.COMPLETED,
    FetchStatus.FAILED,
  ].includes(bookingStatus);

  // Outreach has ended the journey: the lead-status poll came back with a
  // terminal status. The poll keeps running through every hand-over, so the
  // first ending wins: a terminal status that lands while another ending is
  // already underway, or once the booking call has been sent (in flight or
  // settled), is left to that ending. Decided once, when the status arrives,
  // and looked at again if a hand-over is cancelled by Back.
  const isLeadFailureTheFirstEnding = () =>
    hasLeadStatusFailed && !isFallingThrough && !isBookingInFlightOrSettled;
  const [hasLeadFailedBeforeBooking, setHasLeadFailedBeforeBooking] = useState(
    isLeadFailureTheFirstEnding
  );
  useEffect(() => {
    if (isLeadFailureTheFirstEnding()) setHasLeadFailedBeforeBooking(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasLeadStatusFailed, isFallingThrough, isBookingInFlightOrSettled]);

  // Every failure ends the journey: the inactivity timer stops and the
  // progress bar has nothing left to track.
  const hasJourneyFailed =
    journeyTimedOut ||
    hasLeadFailedBeforeBooking ||
    hasNoLocationToBookAt ||
    isFallingThrough;

  const shouldRunInactivityTimer =
    !isOnJourneyEndScreen(screenSequence, position) &&
    !isBookingInFlightOrSettled &&
    !hasJourneyFailed &&
    !bookingOutcome;

  useClientApiInactivityTimer({ shouldRun: shouldRunInactivityTimer });

  // The journey is over -> show the brief message, then continue as a normal
  // lead.
  useFallThroughToNormalLead({
    shouldFallThrough:
      journeyTimedOut || hasLeadFailedBeforeBooking || hasNoLocationToBookAt,
    reason: journeyFallThroughReason({
      journeyTimedOut,
      hasLeadFailedBeforeBooking,
      locationsStatus,
    }),
  });

  const Screen = SCREEN_COMPONENTS[currentScreen?.screenId];

  // Before the mount effect has laid the journey out there is no screen to
  // draw. Mounting PageFade on nothing would make the first screen fade in from
  // a blank view, so hold the render back for that one tick instead.
  if (
    !journeyTimedOut &&
    !hasLeadFailedBeforeBooking &&
    !hasNoLocationToBookAt &&
    !Screen
  )
    return null;

  let content;
  let viewKey;

  // Above the screen lookup: a timeout or a failed lead on any screen has no
  // other render site, so those screens would show nothing at all.
  if (journeyTimedOut) {
    viewKey = 'timedOut';
    content = (
      <FailureNotice
        heading={copy.review.bookingFailed.heading}
        subheading={copy.review.bookingFailed.subheading}
        dataTestId="bookingTimedOut"
      />
    );
  } else if (hasLeadFailedBeforeBooking) {
    const leadFailedCopy = getLeadFailedCopy(copy, leadStatusMessageKey);
    viewKey = 'leadStatusFailed';
    content = (
      <FailureNotice
        heading={leadFailedCopy.heading}
        subheading={leadFailedCopy.subheading}
        dataTestId="leadStatusFailed"
      />
    );
  } else if (hasNoLocationToBookAt) {
    // An empty result has its own wording where the step defines one (the
    // address step); otherwise it reads as a failed lookup.
    const failureCopy =
      (hasNoLocationsFound && locationStepCopy.noResults) ||
      locationStepCopy.lookupFailed;
    viewKey = 'fallThrough';
    content = (
      <FailureNotice
        heading={failureCopy.heading}
        subheading={failureCopy.subheading}
        dataTestId="storeLookupFailed"
      />
    );
  } else {
    viewKey = `${currentScreen.stepIndex}:${currentScreen.screenIndex}`;
    content = (
      <Screen
        componentId={componentId}
        iFrameId={iFrameId}
        stepKey={currentScreen.stepKey}
      />
    );
  }

  return (
    <>
      {!hasJourneyFailed && <JourneyProgress />}
      <PageFade viewKey={viewKey}>{content}</PageFade>
      <InactivityPrompt />
    </>
  );
};

export default ClientApiJourney;
